import { useEffect } from 'react'

const LEADER_TIMEOUT_MS = 1000

export type ShortcutHandler = () => void

export interface ShortcutBinding {
  /** The shortcut string, e.g. `g o`, `?`, `Cmd+K`, `Cmd+Shift+D`. */
  combo: string
  handler: ShortcutHandler
}

type ParsedCombo =
  | { kind: 'single'; key: string; ctrl: boolean; shift: boolean; alt: boolean; meta: boolean }
  | { kind: 'sequence'; keys: string[] }

function parseCombo(combo: string): ParsedCombo {
  // Leader-key sequence — whitespace-separated plain keys.
  if (combo.includes(' ')) {
    return { kind: 'sequence', keys: combo.split(/\s+/).map((k) => k.toLowerCase()) }
  }
  // Single — may include modifiers joined by `+`.
  const parts = combo.split('+').map((p) => p.trim())
  const key = parts.pop()!.toLowerCase()
  const mods = new Set(parts.map((m) => m.toLowerCase()))
  return {
    kind: 'single',
    key,
    ctrl: mods.has('ctrl'),
    shift: mods.has('shift'),
    alt: mods.has('alt'),
    meta: mods.has('cmd') || mods.has('meta'),
  }
}

function isTypingElement(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (el.isContentEditable) return true
  return false
}

/**
 * Install global keyboard shortcuts. Supports:
 * - Single keys with optional modifiers:  `Cmd+K`, `?`, `Cmd+Shift+D`
 * - Leader-key sequences:                 `g o`, `g p`, `f o`
 *
 * Typing in inputs / textareas / contenteditables is ignored unless the
 * combo includes a modifier (Cmd/Ctrl/Alt) — preserves `⌘K` from an
 * input but suppresses `g o` while typing an SO#.
 *
 * Sequences time out after 1s of inactivity.
 */
export function registerShortcuts(bindings: ShortcutBinding[]): () => void {
  const parsed = bindings.map((b) => ({ ...b, parsed: parseCombo(b.combo) }))
  let pendingLeader: string | null = null
  let leaderTimeout: ReturnType<typeof setTimeout> | null = null

  const clearLeader = () => {
    pendingLeader = null
    if (leaderTimeout) {
      clearTimeout(leaderTimeout)
      leaderTimeout = null
    }
  }

  const onKey = (e: KeyboardEvent) => {
    const key = e.key.toLowerCase()
    const hasModifier = e.metaKey || e.ctrlKey || e.altKey
    const typing = isTypingElement(e.target)

    // 1) Match single-key / modifier combos first.
    for (const b of parsed) {
      if (b.parsed.kind !== 'single') continue
      if (typing && !hasModifier) continue
      if (b.parsed.key !== key) continue
      if (b.parsed.ctrl !== e.ctrlKey) continue
      if (b.parsed.shift !== e.shiftKey) continue
      if (b.parsed.alt !== e.altKey) continue
      if (b.parsed.meta !== e.metaKey) continue
      e.preventDefault()
      clearLeader()
      b.handler()
      return
    }

    // Modifier-bearing events can't continue a leader sequence.
    if (hasModifier) return
    // Sequences are suppressed while typing.
    if (typing) return

    // 2) Leader sequence matching.
    if (pendingLeader) {
      const match = parsed.find(
        (b) =>
          b.parsed.kind === 'sequence' &&
          b.parsed.keys.length === 2 &&
          b.parsed.keys[0] === pendingLeader &&
          b.parsed.keys[1] === key,
      )
      clearLeader()
      if (match) {
        e.preventDefault()
        match.handler()
      }
      return
    }

    // 3) Start a sequence — only if some binding uses `key` as the leader.
    const startsWithLeader = parsed.some(
      (b) => b.parsed.kind === 'sequence' && b.parsed.keys[0] === key,
    )
    if (startsWithLeader) {
      pendingLeader = key
      leaderTimeout = setTimeout(clearLeader, LEADER_TIMEOUT_MS)
    }
  }

  window.addEventListener('keydown', onKey)
  return () => {
    window.removeEventListener('keydown', onKey)
    clearLeader()
  }
}

/**
 * React wrapper around `registerShortcuts`. Bindings are re-registered
 * whenever the dep array changes — pass a stable `bindings` reference
 * (memoized via `useMemo`) to avoid churn.
 */
export function useKeyboardShortcuts(bindings: ShortcutBinding[]): void {
  useEffect(() => registerShortcuts(bindings), [bindings])
}
